import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MdDelete, MdEditSquare } from "react-icons/md";
import { FiPlus } from "react-icons/fi";
import Nav from "components/layout/Nav";
import { MenuBar } from "components/layout/MenuBar";
import {
  useGetSubAdminByIdQuery,
  useGetEditorsQuery,
  useCreateEditorMutation,
  useUpdateEditorMutation,
  useDeleteEditorMutation,
  useGetRolesQuery,
} from "Slices/rollManager/subAdmin.slice";
import EditorForm from "components/forms/editorForm";

const InfoRow = ({ label, value }) => (
  <div style={{ display: "flex", gap: "12px", padding: "10px 0", borderBottom: "1px solid #e5e7eb" }}>
    <div style={{ width: 140, color: "#64748b", fontWeight: 600 }}>{label}</div>
    <div style={{ color: "#0f172a", fontWeight: 600 }}>{value || "N/A"}</div>
  </div>
);

const EditorsTable = ({ editors, onEdit, onDelete }) => {
  if (!editors || editors.length === 0) {
    return <p style={{ margin: 0, color: "#64748b" }}>No editors added yet.</p>;
  }

  return (
    <div className="tableWrapper">
      <div className="tableContainer">
        <table border="1">
          <thead>
            <tr>
              <th className="srNo">Sr. No</th>
              <th>Editor ID</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Role</th>
              <th>Segments</th>
              <th>Last Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {editors.map((editor, index) => (
              <tr key={editor.id}>
                <td>{index + 1}</td>
                <td>{editor.id}</td>
                <td>{editor.name || "N/A"}</td>
                <td>{editor.contactNumber || editor.contactInfo || "N/A"}</td>
                <td>{editor.location || editor.district || "N/A"}</td>
                <td>{editor.role?.name || "N/A"}</td>
                <td style={{ maxWidth: 260 }}>
                  {editor.assignedSections?.length
                    ? editor.assignedSections.join(", ")
                    : "Not assigned"}
                </td>
                <td style={{ width: "220px" }}>
                  {editor.updatedAt ? new Date(editor.updatedAt).toLocaleString() : "—"}
                </td>
                <td style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <button
                    className="btn-blue"
                    style={{ padding: "6px 12px", borderRadius: "8px" }}
                    onClick={() => onEdit(editor)}
                  >
                    <MdEditSquare style={{ marginRight: 6 }} />
                    Edit
                  </button>
                  <MdDelete
                    className="icon-24"
                    title="Delete"
                    onClick={() => onDelete(editor.id)}
                    style={{ cursor: "pointer", color: "red" }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RoleProfile = () => {
  const { id } = useParams();
  const { data: subAdmin, isLoading, error } = useGetSubAdminByIdQuery(id, { skip: !id });
  const {
    data: editors = [],
    isLoading: editorsLoading,
    isFetching: editorsFetching,
    error: editorsError,
  } = useGetEditorsQuery(id, { skip: !id });
  const { data: roles = [] } = useGetRolesQuery();

  const [createEditor] = useCreateEditorMutation();
  const [updateEditor] = useUpdateEditorMutation();
  const [deleteEditor] = useDeleteEditorMutation();

  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingEditor, setEditingEditor] = useState(null);

  const handleSaveEditor = async (payload) => {
    try {
      const dataToSend = { ...payload };

      // Do not send password if unchanged in edit mode
      if (editingEditor && !dataToSend.password) {
        delete dataToSend.password;
      }

      if (editingEditor) {
        delete dataToSend.id; // id is in the path for update
        await updateEditor({
          subAdminId: id,
          editorId: editingEditor.id,
          ...dataToSend,
        }).unwrap();
        alert("✅ Editor updated successfully!");
      } else {
        await createEditor({ subAdminId: id, ...dataToSend }).unwrap();
        alert("✅ Editor created successfully!");
      }

      setEditorModalOpen(false);
      setEditingEditor(null);
    } catch (err) {
      const message = err?.data?.message || "Failed to save editor. Please try again.";
      alert(`❌ ${message}`);
      console.error("Error saving editor:", err);
    }
  };

  const handleDeleteEditor = async (editorId) => {
    const confirmed = window.confirm("Are you sure you want to delete this editor? Their edits will stay intact.");
    if (!confirmed) return;
    try {
      await deleteEditor({ subAdminId: id, editorId }).unwrap();
      alert("✅ Editor deleted. Existing edits remain in the system.");
    } catch (err) {
      const message = err?.data?.message || "Failed to delete editor.";
      alert(`❌ ${message}`);
      console.error("Error deleting editor:", err);
    }
  };

  return (
    <>
      <Nav />
      <div className="row">
        <MenuBar />
        <main style={{ width: "calc(100vw - 240px)", backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
          <div style={{ padding: "32px", display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <p style={{ margin: 0, color: "#64748b", fontWeight: 600 }}>Profile</p>
                <h2 style={{ margin: "6px 0 0", fontWeight: 700, color: "#0f172a" }}>Location Manager</h2>
                <p style={{ margin: "6px 0 0", color: "#94a3b8" }}>Account ID: {id}</p>
              </div>
              <Link to="/roleManager" className="btn-blue" style={{ padding: "10px 16px", borderRadius: "8px" }}>
                Back to list
              </Link>
            </div>

            <div
              style={{
                background: "#ffffff",
                borderRadius: "16px",
                padding: "18px",
                boxShadow: "0 12px 40px rgba(15, 23, 42, 0.08)",
              }}
            >
              {isLoading && <p>Loading profile...</p>}
              {error && <p style={{ color: "red" }}>❌ Error loading profile</p>}
              {subAdmin && (
                <>
                  <InfoRow label="Name" value={subAdmin.name} />
                  <InfoRow label="Role" value={subAdmin.role?.name || "Not assigned"} />
                  <InfoRow label="Contact" value={subAdmin.contactNumber} />
                  <InfoRow label="Location" value={subAdmin.location || subAdmin.district} />
                  <InfoRow
                    label="Segments"
                    value={
                      subAdmin.assignedSections?.length
                        ? subAdmin.assignedSections.join(", ")
                        : "No segments assigned"
                    }
                  />
                  <InfoRow label="Login ID" value={subAdmin.id} />
                </>
              )}
            </div>

            <div
              style={{
                background: "#ffffff",
                borderRadius: "16px",
                padding: "18px",
                boxShadow: "0 12px 40px rgba(15, 23, 42, 0.08)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginBottom: "10px",
                }}
              >
                <div>
                  <h3 style={{ margin: 0, fontWeight: 700 }}>Editors</h3>
                  <p style={{ margin: "4px 0 0", color: "#64748b" }}>
                    Add, update, or remove editors for this location manager. Content stays intact even if a user is removed.
                  </p>
                </div>
                <button
                  className="btn-blue"
                  style={{ padding: "10px 14px", borderRadius: "10px", display: "flex", alignItems: "center", gap: "8px" }}
                  onClick={() => {
                    setEditingEditor(null);
                    setEditorModalOpen(true);
                  }}
                >
                  <FiPlus /> Add Editor
                </button>
              </div>

              {editorsLoading || editorsFetching ? (
                <p>Loading editors...</p>
              ) : editorsError ? (
                <p style={{ color: "red" }}>❌ Unable to load editors.</p>
              ) : (
                <EditorsTable editors={editors} onEdit={(ed) => { setEditingEditor(ed); setEditorModalOpen(true); }} onDelete={handleDeleteEditor} />
              )}
            </div>
          </div>
        </main>
      </div>

      <EditorForm
        show={editorModalOpen}
        onClose={() => {
          setEditorModalOpen(false);
          setEditingEditor(null);
        }}
        onSubmit={handleSaveEditor}
        initialData={editingEditor}
      />
    </>
  );
};

export default RoleProfile;
